/**
 * The top bar. Visual Reference: `_system-sheet.html` §05 — "Workspace
 * switcher, plan usage, alerts, avatar menu open".
 *
 * `dashboard.html` is the screen and `_system-sheet.html` §05 is the specimen;
 * where they differ the screen wins, as with the sidebar. The screen spans the
 * bar across the FULL width above the sidebar with a `border-bottom` and no
 * radius, groups the right at `--mdx-space-10` rather than 12, and labels the
 * pill "submissions" with thousands separators — "4,120 / 5,000 submissions".
 *
 * `--mdx-topbar-height`, white on `--mdx-line`, `0 16px`, space-between. Left:
 * the workspace switcher, a `--mdx-space-22` brand square then the name then a
 * caret. Right, on `--mdx-space-12`: the plan pill (13px 600 slate on
 * `--mdx-ground`, `7px 12px`, tabular figures), the alert bell
 * (`--mdx-avatar-lg` square, radius `--mdx-radius-control`, badge in
 * `--mdx-error`), and the avatar (`--mdx-avatar-lg`, `--mdx-radius-pill`,
 * brand, 14px 700 initials).
 *
 * EVERY CONTROL IS ROLE-GATED per PRD §7.3, and gated on the same server-side
 * facts the API enforces with — RULE-009: "UI hiding is presentation only,
 * never enforcement." Nothing here is a control; every one of these has a
 * matching check in `lib/route-contract.ts`.
 *
 *   Workspace switcher   present when the caller holds more than one workspace
 *   Organization switcher Owner and Admin, on multi-org accounts
 *   Plan usage           Owner, Admin, Editor
 *   Needs-attention      Owner, Admin, Editor, Reviewer
 *   Voice wallet         NOT RENDERED — M5, DECISION-016
 *
 * Story 3.8.
 */
import { Bell, ChevronDown, Menu as MenuIcon } from 'lucide-react'
import { useAttentionCount } from './attention'
import { Menu, MenuButton, MenuChoice, MenuHeader, MenuLink } from './Menu'
import type { Me, MeOrg, MeWorkspace } from './useMe'

/** Two letters, from a name if there is one and an address if there is not. */
export function initials(name: string, email: string): string {
  const source = name.trim() || email.split('@')[0] || ''
  const parts = source.split(/[\s._-]+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase()
  return source.slice(0, 2).toUpperCase()
}

export function TopBar({
  me,
  workspace,
  org,
  onWorkspace,
  onOrg,
  onSignOut,
  onOpenNav,
}: {
  me: Me
  workspace: MeWorkspace | undefined
  org: MeOrg | undefined
  onWorkspace: (id: string) => void
  onOrg: (id: string) => void
  onSignOut: () => void
  onOpenNav: () => void
}) {
  const orgRole = org?.role
  const workspaceRole = workspace?.role
  // Role names come from the server's own vocabulary — `member.role` for org
  // scope, `workspace_access.role` for workspace scope (DECISION-002).
  const atLeastEditor =
    orgRole === 'owner' || orgRole === 'admin' || workspaceRole === 'editor'
  const atLeastReviewer = atLeastEditor || workspaceRole === 'reviewer'

  /** Story 12.2's feed count, filled by the dashboard's one call. */
  const attention = useAttentionCount()

  const entitlements = org ? me.entitlements[org.id] : undefined
  const submissionCap = entitlements?.limits['submissions_per_month']

  return (
    <header className="flex h-mdx-topbar shrink-0 items-center justify-between gap-mdx-12 border-b border-mdx-line bg-mdx-white px-mdx-16">
      <div className="flex min-w-0 items-center gap-mdx-8">
        <button
          type="button"
          aria-label="Open navigation"
          onClick={onOpenNav}
          className="flex size-mdx-avatar-lg shrink-0 cursor-pointer items-center justify-center rounded-mdx-control border border-mdx-line bg-mdx-white transition-colors duration-mdx-swift ease-mdx-swift hover:bg-mdx-ground lg:hidden"
        >
          <MenuIcon aria-hidden="true" className="size-mdx-16" strokeWidth={2} />
        </button>

        {/* PRD §7.3: present whenever the user has access to more than one. */}
        {me.workspaces.length > 1 ? (
          <Menu
            label="Switch workspace"
            align="left"
            trigger={
              <>
                <span
                  aria-hidden="true"
                  className="size-mdx-22 shrink-0 rounded-mdx-sm bg-mdx-brand"
                />
                <span className="truncate">{workspace?.name ?? 'Choose a workspace'}</span>
                <ChevronDown aria-hidden="true" className="size-mdx-16 text-mdx-slate" strokeWidth={2} />
              </>
            }
          >
            {me.workspaces.map((w) => (
              <MenuChoice
                key={w.id}
                selected={w.id === workspace?.id}
                onClick={() => onWorkspace(w.id)}
              >
                {w.name}
              </MenuChoice>
            ))}
          </Menu>
        ) : (
          <span className="flex min-w-0 items-center gap-mdx-8 px-mdx-12 py-mdx-9 text-mdx-15 font-mdx-semibold text-mdx-ink">
            <span aria-hidden="true" className="size-mdx-22 shrink-0 rounded-mdx-sm bg-mdx-brand" />
            <span className="truncate">{workspace?.name ?? 'Leadgible'}</span>
          </span>
        )}

        {/* PRD §7.3: agency accounts spanning multiple orgs. Owner and Admin. */}
        {me.orgs.length > 1 && (orgRole === 'owner' || orgRole === 'admin') ? (
          <Menu
            label="Switch organisation"
            align="left"
            trigger={
              <>
                <span className="truncate text-mdx-slate">{org?.name}</span>
                <ChevronDown aria-hidden="true" className="size-mdx-16 text-mdx-slate" strokeWidth={2} />
              </>
            }
          >
            {me.orgs.map((o) => (
              <MenuChoice key={o.id} selected={o.id === org?.id} onClick={() => onOrg(o.id)}>
                {o.name}
              </MenuChoice>
            ))}
          </Menu>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-mdx-10">
        {/*
          Submissions month-to-date against the limit (PRD §7.3, §4.5). The cap
          is resolved now; the COUNT arrives with Story 10.5's usage counters,
          and until then a fresh org's 0 is the true number rather than a
          placeholder. Hidden below 1024px, where the bar has no room for it.
        */}
        {atLeastEditor && submissionCap !== undefined ? (
          <span className="hidden whitespace-nowrap rounded-mdx-control border border-mdx-line bg-mdx-ground px-mdx-12 py-mdx-7 text-mdx-13 font-mdx-semibold tabular-nums text-mdx-slate lg:inline">
            {submissionCap === -1
              ? 'Unlimited submissions'
              : `0 / ${submissionCap.toLocaleString('en-GB')} submissions`}
          </span>
        ) : null}

        {/*
          THE BADGE IS THE FEED'S OWN COUNT — Story 12.2. It comes from the one
          `GET …/dashboard` the contract specifies rather than from a second
          fetch, through `shell/attention.ts`; `/dashboard` is `HOME_PATH`, so
          every session fills it on its first page.

          `null` is not zero and does not draw. An unknown count rendered as `0`
          would tell somebody nothing needs them on the strength of never having
          looked, which is the one thing a bell must not do.
        */}
        {atLeastReviewer ? (
          <a
            href="/dashboard"
            aria-label={
              attention === null
                ? 'Alerts'
                : attention === 0
                  ? 'Alerts, none unread'
                  : `Alerts, ${attention} unread`
            }
            className="relative flex size-mdx-avatar-lg items-center justify-center rounded-mdx-control border border-mdx-line bg-mdx-white transition-colors duration-mdx-swift ease-mdx-swift hover:bg-mdx-ground"
          >
            <Bell aria-hidden="true" className="size-mdx-16 text-mdx-ink" strokeWidth={2} />
            {attention !== null && attention > 0 ? (
              <span
                aria-hidden="true"
                className="absolute -top-mdx-6 -right-mdx-6 flex h-mdx-18 min-w-mdx-18 items-center justify-center rounded-mdx-pill bg-mdx-error px-mdx-5 text-mdx-11 font-mdx-bold tabular-nums text-mdx-white"
              >
                {attention}
              </span>
            ) : null}
          </a>
        ) : null}

        <Menu
          label="Account"
          // The sheet's avatar is a bare 38px circle, not the padded pill the
          // switchers use, so this trigger supplies its own box.
          triggerClassName="cursor-pointer rounded-mdx-pill border-0 bg-transparent p-0"
          trigger={
            <span className="flex size-mdx-avatar-lg items-center justify-center rounded-mdx-pill bg-mdx-brand text-mdx-14 font-mdx-bold text-mdx-white">
              {initials(me.user.name, me.user.email)}
            </span>
          }
        >
          <MenuHeader title={me.user.name || me.user.email} subtitle={me.user.email} />
          <MenuLink href="/settings#profile">Profile</MenuLink>
          <MenuLink href="/settings">Settings</MenuLink>
          <MenuButton onClick={onSignOut}>Log out</MenuButton>
        </Menu>
      </div>
    </header>
  )
}
